import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { fetchClinicianClientsReport } from '../../../../store/thunks/reportsThunk';
import { fetchClientById } from '../../../../store/thunks/clientsThunk';
import ClientModal from '../../../../modals/ClientModal';
import { toast } from 'react-toastify';

function ClinicianClientsReport() {
  const [reportData, setReportData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedClinicians, setExpandedClinicians] = useState(new Set());
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    handleGenerateReport();
  }, []);

  const handleGenerateReport = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(fetchClinicianClientsReport()).unwrap();
      setReportData(response.data);
      setSummary(response.summary);
      toast.success('Clinician clients report generated successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to generate report');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleClinician = (clinicianId) => {
    const newExpanded = new Set(expandedClinicians);
    if (newExpanded.has(clinicianId)) {
      newExpanded.delete(clinicianId);
    } else {
      newExpanded.add(clinicianId);
    }
    setExpandedClinicians(newExpanded);
  };

  const handleClientClick = (clientId) => {
    dispatch(fetchClientById(clientId));
    setIsClientModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsClientModalOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      recall: 'bg-yellow-100 text-yellow-800',
      discharged: 'bg-blue-100 text-blue-800',
      other: 'bg-purple-100 text-purple-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-sm shadow p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-600">Loading report...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-sm shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Clinician-Client Relationships Report</h2>
          <button
            onClick={handleGenerateReport}
            disabled={isLoading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-sm hover:bg-indigo-700 disabled:bg-gray-400"
          >
            Refresh Report
          </button>
        </div>

        {/* Summary Statistics */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-indigo-50 p-4 rounded-sm">
              <div className="text-sm text-indigo-600 font-medium">Total Clinicians</div>
              <div className="text-2xl font-bold text-indigo-900">{summary.totalClinicians}</div>
            </div>
            <div className="bg-green-50 p-4 rounded-sm">
              <div className="text-sm text-green-600 font-medium">Treating Relationships</div>
              <div className="text-2xl font-bold text-green-900">{summary.totalTreatingRelationships}</div>
            </div>
            <div className="bg-blue-50 p-4 rounded-sm">
              <div className="text-sm text-blue-600 font-medium">Supervising Relationships</div>
              <div className="text-2xl font-bold text-blue-900">{summary.totalSupervisingRelationships}</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-sm">
              <div className="text-sm text-purple-600 font-medium">Avg Treating/Clinician</div>
              <div className="text-2xl font-bold text-purple-900">{summary.averageTreatingPerClinician}</div>
            </div>
            <div className="bg-pink-50 p-4 rounded-sm">
              <div className="text-sm text-pink-600 font-medium">Avg Supervising/Clinician</div>
              <div className="text-2xl font-bold text-pink-900">{summary.averageSupervisingPerClinician}</div>
            </div>
          </div>
        )}

        {/* Clinician List */}
        {reportData && reportData.length > 0 ? (
          <div className="space-y-4">
            {reportData.map((clinicianData) => {
              const isExpanded = expandedClinicians.has(clinicianData.clinician.id);
              const hasClients = clinicianData.counts.total > 0;

              return (
                <div key={clinicianData.clinician.id} className="border rounded-sm overflow-hidden">
                  {/* Clinician Header */}
                  <div
                    onClick={() => hasClients && toggleClinician(clinicianData.clinician.id)}
                    className={`p-4 bg-gray-50 flex justify-between items-center ${
                      hasClients ? 'cursor-pointer hover:bg-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {hasClients && (
                        <svg
                          className={`w-5 h-5 text-gray-500 transition-transform ${
                            isExpanded ? 'transform rotate-90' : ''
                          }`}
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M9 5l7 7-7 7"></path>
                        </svg>
                      )}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {clinicianData.clinician.name}
                        </h3>
                        <p className="text-sm text-gray-500">{clinicianData.clinician.email}</p>
                      </div>
                    </div>
                    <div className="flex space-x-4">
                      <div className="text-center">
                        <div className="text-sm text-gray-600">Treating</div>
                        <div className="text-xl font-bold text-green-600">
                          {clinicianData.counts.treating}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600">Supervising</div>
                        <div className="text-xl font-bold text-blue-600">
                          {clinicianData.counts.supervising}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600">Total</div>
                        <div className="text-xl font-bold text-indigo-600">
                          {clinicianData.counts.total}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Client Details */}
                  {isExpanded && hasClients && (
                    <div className="p-4 bg-white">
                      {/* Treating Clients Section */}
                      {clinicianData.treatingClients.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-md font-semibold text-green-700 mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Treating Clients ({clinicianData.treatingClients.length})
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-green-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Client Name
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Date of Birth
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {clinicianData.treatingClients.map((client) => (
                                  <tr
                                    key={client.id}
                                    onClick={() => handleClientClick(client.id)}
                                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                                  >
                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {client.name}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                      {formatDate(client.dateOfBirth)}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(client.status)}`}>
                                        {client.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Supervising Clients Section */}
                      {clinicianData.supervisingClients.length > 0 && (
                        <div>
                          <h4 className="text-md font-semibold text-blue-700 mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Supervising Clients ({clinicianData.supervisingClients.length})
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-blue-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Client Name
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Date of Birth
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {clinicianData.supervisingClients.map((client) => (
                                  <tr
                                    key={client.id}
                                    onClick={() => handleClientClick(client.id)}
                                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                                  >
                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {client.name}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                      {formatDate(client.dateOfBirth)}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(client.status)}`}>
                                        {client.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* No Clients Message */}
                      {clinicianData.treatingClients.length === 0 &&
                       clinicianData.supervisingClients.length === 0 && (
                        <p className="text-gray-500 text-center py-4">
                          No clients assigned to this clinician
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No clinician data available
          </div>
        )}
      </div>

      {/* Client Modal */}
      <ClientModal
        isOpen={isClientModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}

export default ClinicianClientsReport;
